import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Wrench, Zap, Utensils, Stethoscope, Car, Scissors, Building, 
  ChevronRight, MapPin, TrendingUp, Users, CheckCircle, Star, Quote,
  Newspaper, ExternalLink, Clock
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SearchBar } from "@/components/SearchBar";
import { BusinessCard } from "@/components/BusinessCard";
import { Button } from "@/components/ui/button";
import { businessesApi, categoriesApi, statsApi, seedApi, newsApi } from "@/lib/api";

const categoryIcons = {
  "plomberie": Wrench,
  "electricite": Zap,
  "restaurant": Utensils,
  "medecin": Stethoscope,
  "garage-automobile": Car,
  "coiffeur": Scissors,
  "agence-immobiliere": Building,
};

// Optimize image URLs for faster loading
const optimizeImageUrl = (url, width = 400) => {
  if (!url) return url;
  
  // Pexels: add auto=compress and w parameter
  if (url.includes('pexels.com')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}auto=compress&cs=tinysrgb&w=${width}&dpr=1`;
  }
  
  // Unsplash: add w and q parameters
  if (url.includes('unsplash.com')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=${width}&q=75&fm=webp`;
  }
  
  return url;
};

export default function HomePage() {
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ businesses: 0, categories: 0, departments: 0 });
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    const initData = async () => {
      try {
        // Seed database first
        await seedApi.seed();
        
        // Fetch data
        const [businessesRes, categoriesRes, statsRes] = await Promise.all([
          businessesApi.getFeatured(6),
          categoriesApi.getAll(),
          statsApi.get(),
        ]);

        setFeaturedBusinesses(businessesRes.data);
        setCategories(categoriesRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch news separately (non-blocking)
    const fetchNews = async () => {
      try {
        const newsRes = await newsApi.getNews(6);
        setNews(newsRes.data.news || []);
      } catch (error) {
        console.error("Error loading news:", error);
      } finally {
        setNewsLoading(false);
      }
    };

    initData();
    fetchNews();
  }, []);

  const popularCategories = categories.slice(0, 8);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      <Header variant="transparent" />

      {/* Hero Section */}
      <section className="hero-section relative pt-24 pb-20 md:pt-32 md:pb-28">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
              Trouvez les meilleurs
              <span className="text-[#FFD700]"> professionnels</span>
              <br />près de chez vous
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl">
              L'annuaire professionnel de référence avec plus de {stats.businesses} entreprises dans {stats.departments} départements français.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mt-10 max-w-4xl">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <SearchBar variant="large" />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-10 flex flex-wrap gap-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                <Building className="w-6 h-6 text-[#FFD700]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.businesses}+</p>
                <p className="text-sm text-slate-400">Entreprises</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#2563EB]/20 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.departments}</p>
                <p className="text-sm text-slate-400">Départements</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.categories}</p>
                <p className="text-sm text-slate-400">Catégories</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                Catégories populaires
              </h2>
              <p className="mt-2 text-slate-600">Trouvez rapidement ce que vous cherchez</p>
            </div>
            <Link 
              to="/categories"
              className="hidden md:flex items-center gap-1 text-[#2563EB] font-semibold hover:underline"
              data-testid="view-all-categories"
            >
              Voir toutes
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {popularCategories.map((category, idx) => {
              return (
                <Link
                  key={category.id}
                  to={`/recherche?category_id=${category.id}`}
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-[#FFD700] hover:shadow-lg transition-all"
                  style={{ animationDelay: `${idx * 50}ms` }}
                  data-testid={`category-${category.slug}`}
                >
                  {/* Image vignette */}
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    {category.image_url ? (
                      <img
                        src={optimizeImageUrl(category.image_url, 400)}
                        alt={category.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                  </div>
                  {/* Nom catégorie */}
                  <div className="p-3 bg-white">
                    <h3 className="font-semibold text-slate-900 text-sm text-center group-hover:text-[#2563EB]">
                      {category.name}
                    </h3>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-6 md:hidden text-center">
            <Link 
              to="/categories"
              className="inline-flex items-center gap-1 text-[#2563EB] font-semibold"
            >
              Voir toutes les catégories
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Businesses */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                Entreprises à la une
              </h2>
              <p className="mt-2 text-slate-600">Découvrez nos partenaires de confiance</p>
            </div>
            <Link 
              to="/recherche"
              className="hidden md:flex items-center gap-1 text-[#2563EB] font-semibold hover:underline"
              data-testid="view-all-businesses"
            >
              Voir plus
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex gap-4">
                    <div className="skeleton w-20 h-20 rounded-lg"></div>
                    <div className="flex-1 space-y-3">
                      <div className="skeleton h-5 w-3/4"></div>
                      <div className="skeleton h-4 w-1/4"></div>
                      <div className="skeleton h-4 w-full"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {featuredBusinesses.map((business) => {
                const category = categories.find(c => c.id === business.category_id);
                return (
                  <BusinessCard 
                    key={business.id} 
                    business={business} 
                    category={category}
                  />
                );
              })}
            </div>
          )}

          <div className="mt-8 text-center">
            <Link to="/recherche">
              <Button className="bg-[#FFD700] text-slate-900 font-bold hover:bg-[#E6C200] px-8" data-testid="explore-all-btn">
                Explorer toutes les entreprises
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              Ce que disent nos <span className="text-[#FFD700]">utilisateurs</span>
            </h2>
            <p className="mt-2 text-slate-600">Des milliers de clients satisfaits partout en France</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Review 1 */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 relative">
              <Quote className="absolute top-4 right-4 w-8 h-8 text-[#FFD700]/30" />
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic">
                "J'ai trouvé un excellent plombier en moins de 5 minutes ! Il est intervenu le jour même. 
                Je recommande vivement cet annuaire."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#2563EB] flex items-center justify-center text-white font-bold">
                  ML
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Marie L.</p>
                  <p className="text-sm text-slate-500">Paris 15ème</p>
                </div>
              </div>
            </div>

            {/* Review 2 */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 relative">
              <Quote className="absolute top-4 right-4 w-8 h-8 text-[#FFD700]/30" />
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic">
                "Super pratique ! La recherche par géolocalisation m'a permis de trouver un garage 
                à 500m de chez moi. Service impeccable."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                  PD
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Philippe D.</p>
                  <p className="text-sm text-slate-500">Lyon 3ème</p>
                </div>
              </div>
            </div>

            {/* Review 3 */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 relative">
              <Quote className="absolute top-4 right-4 w-8 h-8 text-[#FFD700]/30" />
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < 4 ? "fill-[#FFD700] text-[#FFD700]" : "text-slate-300"}`} />
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic">
                "Enfin un annuaire moderne et bien organisé ! J'ai pu comparer plusieurs électriciens 
                et choisir celui qui correspondait le mieux à mes besoins."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FFD700] flex items-center justify-center text-slate-900 font-bold">
                  SB
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Sophie B.</p>
                  <p className="text-sm text-slate-500">Marseille</p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust badges */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-slate-500">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium">+10 000 avis vérifiés</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-[#FFD700]" />
              <span className="text-sm font-medium">Note moyenne : 4.7/5</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#2563EB]" />
              <span className="text-sm font-medium">+50 000 utilisateurs mensuels</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Pourquoi choisir Partenaire des Entreprises ?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#FFD700]/20 flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-[#FFD700]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Professionnels vérifiés</h3>
              <p className="text-slate-400">
                Toutes les entreprises sont vérifiées pour garantir un service de qualité.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#2563EB]/20 flex items-center justify-center mb-4">
                <MapPin className="w-8 h-8 text-[#2563EB]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Recherche géolocalisée</h3>
              <p className="text-slate-400">
                Trouvez les professionnels les plus proches de votre position.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Contact direct</h3>
              <p className="text-slate-400">
                Contactez directement les entreprises par téléphone ou via leur site web.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Newspaper className="w-8 h-8 text-[#2563EB]" />
                Actualités économiques
              </h2>
              <p className="mt-2 text-slate-600">Les dernières nouvelles du monde des affaires</p>
            </div>
          </div>

          {newsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="skeleton h-32 rounded-lg mb-4"></div>
                  <div className="skeleton h-5 w-3/4 mb-2"></div>
                  <div className="skeleton h-4 w-full mb-1"></div>
                  <div className="skeleton h-4 w-2/3"></div>
                </div>
              ))}
            </div>
          ) : news.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune actualité disponible pour le moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((item, idx) => (
                <a
                  key={idx}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-[#2563EB] transition-all"
                  data-testid={`news-item-${idx}`}
                >
                  {/* Image */}
                  <div className="h-40 bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    {/* Source badge */}
                    <div className="absolute top-3 left-3 px-2 py-1 bg-white/90 backdrop-blur-sm rounded text-xs font-semibold text-slate-700">
                      {item.source}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-[#2563EB]">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                      {item.summary}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {item.published ? new Date(item.published).toLocaleDateString('fr-FR') : 'Récent'}
                      </span>
                      <span className="flex items-center gap-1 text-[#2563EB] group-hover:underline">
                        Lire plus
                        <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
